import { CheckCircle2, AlertTriangle } from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Modal, cn } from "./ui";

type Brief = { id: string; name: string; internal_id: string; nik: string; unit: string };
type Sync = { students: number; loans: number; matrix: number; synced: boolean; lunas_only?: number; no_loan?: Brief[]; no_matrix?: Brief[]; lunas?: Brief[] };

export function SyncCounter({ highlight, refresh }: { highlight: "students" | "loans" | "matrix"; refresh?: unknown }) {
  const [d, setD] = useState<Sync | null>(null);
  const [open, setOpen] = useState(false);
  useEffect(() => { api.get("/dashboard/sync-count", { params: { detail: 1 } }).then((r) => setD(r.data)).catch(() => setD(null)); }, [refresh]);
  if (!d) return null;
  const items: [keyof Sync, string][] = [["students", "Siswa"], ["loans", "Pinjaman"], ["matrix", "Matriks"]];
  const title = d.synced
    ? `Sinkron: siswa aktif berpinjaman = siswa dengan pinjaman aktif = siswa di matriks cicilan${d.lunas_only ? ` (${d.lunas_only} siswa lunas tidak dihitung)` : ""}`
    : `Tidak sinkron: Siswa ${d.students} · Pinjaman ${d.loans} · Matriks ${d.matrix}. Klik untuk melihat daftar siswa.`;
  return (
    <>
      <button type="button" disabled={d.synced} onClick={() => setOpen(true)} data-testid="sync-counter" data-synced={d.synced} title={title}
        className={cn("inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-semibold", d.synced ? "border-emerald-300 bg-emerald-50 text-emerald-700 cursor-default" : "border-red-300 bg-red-50 text-red-700 hover:bg-red-100 cursor-pointer")}>
        {d.synced ? <CheckCircle2 className="h-3.5 w-3.5" /> : <AlertTriangle className="h-3.5 w-3.5" />}
        {items.map(([k, label], i) => (
          <span key={k} className="flex items-center gap-1">
            {i > 0 && <span className="opacity-40">·</span>}
            <span className={cn(k === highlight && "underline underline-offset-2")}>{label}</span>
            <span data-testid={`sync-count-${k}`} className="tabular-nums">{d[k] as number}</span>
          </span>
        ))}
        <span className="ml-1 hidden sm:inline">{d.synced ? "Sinkron" : "Tidak sinkron"}</span>
      </button>
      <Modal open={open} onClose={() => setOpen(false)} title="Siswa Tidak Sinkron" testId="sync-detail-modal">
        <SyncList label="Siswa aktif tanpa pinjaman aktif" hint="Rekomendasi: buat pinjaman manual (Akun Pinjaman → Pinjaman Baru) atau ubah status siswa menjadi Nonaktif." rows={d.no_loan || []} testId="sync-no-loan" onPick={() => setOpen(false)} />
        <SyncList label="Pinjaman aktif tanpa jadwal cicilan (matriks)" hint="Rekomendasi: jalankan QC Agent di Dashboard untuk membentuk ulang jadwal." rows={d.no_matrix || []} testId="sync-no-matrix" onPick={() => setOpen(false)} />
        <SyncList label="Pinjaman sudah lunas · tidak dihitung selisih" hint="Informasi saja. Ubah status ke Nonaktif/Alumni bila tidak akan meminjam lagi." rows={d.lunas || []} testId="sync-lunas" tone="ok" onPick={() => setOpen(false)} />
      </Modal>
    </>
  );
}

function SyncList({ label, hint, rows, testId, tone, onPick }: { label: string; hint?: string; rows: Brief[]; testId: string; tone?: "ok"; onPick: () => void }) {
  if (!rows.length) return null;
  const rowCls = tone === "ok" ? "bg-emerald-50 dark:bg-emerald-950/30" : "bg-pink-100/80 dark:bg-pink-950/40";
  const nameCls = tone === "ok" ? "text-emerald-800" : "text-red-700";
  return (
    <div className="mb-4" data-testid={testId}>
      <p className="text-xs font-semibold text-muted-foreground">{label} · {rows.length}</p>
      {hint && <p className="text-[11px] text-muted-foreground mb-2">{hint}</p>}
      <div className="max-h-72 overflow-auto rounded-md border">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs text-muted-foreground"><th className="px-2 py-1">ID</th><th className="px-2 py-1">Nama</th><th className="px-2 py-1">NIK</th><th className="px-2 py-1">Unit</th></tr></thead>
          <tbody>
            {rows.map((s) => (
              <tr key={s.id} data-testid={`${testId}-row-${s.internal_id}`} className={cn(rowCls, "border-t")}>
                <td className="px-2 py-1 font-mono text-xs">{s.internal_id}</td>
                <td className="px-2 py-1"><Link to={`/siswa?q=${encodeURIComponent(s.internal_id)}`} onClick={onPick} className={cn("font-medium hover:underline", nameCls)}>{s.name}</Link></td>
                <td className="px-2 py-1 font-mono text-xs">{s.nik}</td>
                <td className="px-2 py-1">{s.unit}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
